import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminModeration() {
  const [reports, setReports] = useState([]);
  const [words, setWords] = useState("");
  const [streams, setStreams] = useState([]);

  const load = useCallback(() => {
    adminApi.get("/admin/reports?status=open").then(({ data }) => setReports(data.reports)).catch(() => {});
    adminApi.get("/admin/moderation/banned-words").then(({ data }) => setWords(data.words.join(", "))).catch(() => {});
    adminApi.get("/streams?tab=popular").then(({ data }) => setStreams(data.streams)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    try {
      await adminApi.post(`/admin/reports/${r.id}/resolve`);
      toast.success("Report resolved");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const kill = async (s) => {
    if (!window.confirm(`Kill "${s.title}" by ${s.host?.name}? Viewers will be disconnected.`)) return;
    try {
      await adminApi.post(`/admin/streams/${s.id}/kill`);
      toast.success("Stream killed");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const saveWords = async () => {
    try {
      await adminApi.put("/admin/moderation/banned-words", { words: words.split(",").map((w) => w.trim()).filter(Boolean) });
      toast.success("Banned words updated — applied to all chats instantly");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div data-testid="admin-moderation">
      <h1 className="text-xl font-extrabold font-display mb-6">Moderation Center</h1>

      <div className="glass rounded-2xl p-5 mb-6">
        <p className="text-sm font-bold mb-2">🚫 Banned words (comma-separated, replaced with *** in chat)</p>
        <div className="flex gap-2">
          <textarea data-testid="banned-words-input" rows={2} value={words} onChange={(e) => setWords(e.target.value)}
            className="flex-1 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-violet-500" />
          <button data-testid="banned-words-save" onClick={saveWords} className="self-end gradient-primary text-white text-xs font-bold px-5 py-2.5 rounded-xl">Save</button>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mb-6">
        <p className="text-sm font-bold mb-3">🚩 Open Reports ({reports.length})</p>
        {reports.length === 0 && <p className="text-zinc-600 text-xs">No open reports</p>}
        <div className="space-y-2">
          {reports.map((r) => (
            <div key={r.id} className="flex items-center justify-between bg-white/5 rounded-xl px-3.5 py-2.5" data-testid={`report-row-${r.id}`}>
              <div className="min-w-0">
                <p className="text-xs text-white font-semibold truncate">{r.target_type === "stream" ? "📺" : "👤"} {r.target_name}</p>
                <p className="text-[10px] text-zinc-500 truncate">"{r.reason}" — reported by {r.reporter} · {new Date(r.created_at).toLocaleString()}</p>
              </div>
              <button data-testid={`report-resolve-${r.id}`} onClick={() => resolve(r)}
                className="bg-white/10 text-zinc-300 text-[10px] font-bold px-3 py-1.5 rounded-lg shrink-0 ml-3 hover:bg-white/20">Resolve</button>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-5">
        <p className="text-sm font-bold mb-3">🔴 Live Streams — Kill Switch</p>
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="pb-2">Host</th><th>Title</th><th>Viewers</th><th></th></tr></thead>
          <tbody>
            {streams.map((s) => (
              <tr key={s.id} className="border-b border-white/5">
                <td className="py-2 text-white font-semibold">{s.host?.name}</td>
                <td className="text-zinc-400 truncate max-w-[240px]">{s.title}</td>
                <td className="text-zinc-300">{s.viewers?.toLocaleString()}</td>
                <td className="text-right">
                  <button data-testid={`kill-stream-${s.id}`} onClick={() => kill(s)}
                    className="bg-rose-500/20 text-rose-400 text-[10px] font-bold px-3 py-1.5 rounded-lg">Kill</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
